import * as EpisodeService from "@/services/EpisodeService"
import { pollTaskStatus } from "@/common/helpers/pollTaskStatus"
import { notificationError, notificationSuccess } from "@/common/helpers/notifications.helper"
import { i18n } from "@/plugins/i18n"

const PROCESSING_POLL = { interval: 1000, backoff: 1.5, maxInterval: 10000, maxErrors: 5 }
const ETA_WARMUP_SECONDS = 1
const PRUNE_DELAY = 5000
const RESAVE_AFTER_ERROR_DELAY = 3000

const files = new Map()
const controllers = new Map()
const pollers = new Map()
const pendingSaves = new Map()
const pruneTimers = new Map()
const discards = new Map()

let unloadGuardActive = false

const handleBeforeUnload = (event) => {
  event.preventDefault()
  event.returnValue = ""
}

const uploadErrorKey = (e) => {
  if (!e?.response) return "episodes.upload_errors.network"

  switch (e.response.status) {
    case 400:
      return "episodes.upload_errors.rejected"
    case 401:
    case 403:
      return "episodes.upload_errors.forbidden"
    case 404:
      return "episodes.upload_errors.not_found"
    case 413:
      return "episodes.upload_errors.too_large"
    default:
      return "episodes.upload_errors.server"
  }
}

const stopTransfer = (episodeKey) => {
  controllers.get(episodeKey)?.abort()
  controllers.delete(episodeKey)
  pollers.get(episodeKey)?.()
  pollers.delete(episodeKey)
  clearTimeout(pruneTimers.get(episodeKey))
  pruneTimers.delete(episodeKey)
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

export const syncUnloadGuard = ({ getters }) => {
  const shouldGuard = getters.hasActiveUploads

  if (shouldGuard === unloadGuardActive) return

  unloadGuardActive = shouldGuard

  if (shouldGuard) {
    window.addEventListener("beforeunload", handleBeforeUnload)
  } else {
    window.removeEventListener("beforeunload", handleBeforeUnload)
  }
}

export const startUpload = async ({ commit, dispatch }, { podcastKey, episodeKey, file, isAiPodcast, title }) => {
  stopTransfer(episodeKey)

  const controller = new AbortController()
  const startedAt = Date.now()

  files.set(episodeKey, file)
  controllers.set(episodeKey, controller)

  commit("setUpload", {
    episodeKey,
    podcastKey,
    isAiPodcast: !!isAiPodcast,
    title: title || file.name,
    fileName: file.name,
    fileSize: file.size,
    status: "uploading",
    progress: 0,
    uploadedBytes: 0,
    totalBytes: file.size,
    secondsLeft: null,
    errorKey: null,
    fileUrl: "",
  })
  dispatch("syncUnloadGuard")

  let response

  try {
    response = await EpisodeService.uploadEpisodeFile(podcastKey, episodeKey, file, {
      signal: controller.signal,
      onUploadProgress: ({ loaded, total }) => {
        if (controller.signal.aborted) return

        const elapsed = (Date.now() - startedAt) / 1000

        commit("updateUpload", {
          episodeKey,
          fields: {
            progress: total ? Math.round((loaded * 100) / total) : 0,
            uploadedBytes: loaded,
            totalBytes: total || file.size,
            secondsLeft:
              total && loaded && elapsed >= ETA_WARMUP_SECONDS ? (total - loaded) / (loaded / elapsed) : null,
          },
        })
      },
    })
  } catch (e) {
    if (controller.signal.aborted) return

    console.error(e)
    controllers.delete(episodeKey)
    dispatch("failUpload", { episodeKey, errorKey: uploadErrorKey(e) })

    return
  }

  if (controller.signal.aborted) return

  controllers.delete(episodeKey)

  if (!response?.task_id) {
    dispatch("failUpload", { episodeKey, errorKey: "episodes.upload_errors.server" })

    return
  }

  commit("updateUpload", { episodeKey, fields: { status: "processing", progress: 100, secondsLeft: null } })

  pollers.set(
    episodeKey,
    pollTaskStatus({
      fetchStatus: () => EpisodeService.getEpisodeUploadingStatus(response.task_id),
      onSuccess: (result) => dispatch("completeUpload", { episodeKey, episode: result.data }),
      onError: () => dispatch("failUpload", { episodeKey, errorKey: "episodes.upload_errors.processing", processed: true }),
      onException: (e) => {
        console.error(e)
        dispatch("failUpload", { episodeKey, errorKey: "episodes.upload_errors.connection_lost" })
      },
      ...PROCESSING_POLL,
    })
  )
}

export const flushPendingSave = async ({ commit, rootGetters, getters }, episodeKey) => {
  const pending = pendingSaves.get(episodeKey)

  if (!pending) return

  pendingSaves.delete(episodeKey)

  try {
    const saved = await EpisodeService.updateEpisode(pending.payload, pending.isAiPodcast)

    if (rootGetters["Episode/episode"]?.key === episodeKey) {
      commit("Episode/setEpisode", saved, { root: true })
    }
  } catch (e) {
    console.error(e)

    const title = getters.uploadFor(episodeKey)?.title ?? pending.payload.title

    notificationError(
      i18n.t("episodes.upload_notifications.resave_failed", { title }),
      i18n.t("episodes.update_error_title")
    )
  }
}

const deleteDiscarded = async ({ commit, dispatch }, episodeKey) => {
  const discard = discards.get(episodeKey)

  discards.delete(episodeKey)
  pendingSaves.delete(episodeKey)
  files.delete(episodeKey)
  commit("removeUpload", episodeKey)
  dispatch("syncUnloadGuard")

  try {
    await EpisodeService.deleteEpisode(discard.podcastKey, episodeKey, discard.isAiPodcast)
  } catch (e) {
    console.error("Failed to delete the discarded episode", e)
  }
}

export const discardEpisode = async (context, { podcastKey, episodeKey, isAiPodcast }) => {
  const { getters } = context

  discards.set(episodeKey, { podcastKey, isAiPodcast })

  if (getters.uploadFor(episodeKey)?.status === "processing") return

  stopTransfer(episodeKey)
  await deleteDiscarded(context, episodeKey)
}

export const completeUpload = async (context, { episodeKey, episode }) => {
  const { commit, dispatch, getters } = context

  pollers.delete(episodeKey)

  if (!getters.uploadFor(episodeKey)) return

  if (discards.has(episodeKey)) {
    await deleteDiscarded(context, episodeKey)
    return
  }

  await dispatch("flushPendingSave", episodeKey)

  const upload = getters.uploadFor(episodeKey)

  if (!upload) return

  files.delete(episodeKey)
  commit("updateUpload", { episodeKey, fields: { status: "completed", fileUrl: episode?.file_url ?? "" } })
  dispatch("syncUnloadGuard")

  notificationSuccess(
    i18n.t("episodes.upload_notifications.completed", { title: upload.title }),
    i18n.t("episodes.upload_success_title")
  )

  pruneTimers.set(
    episodeKey,
    setTimeout(() => {
      pruneTimers.delete(episodeKey)

      if (getters.uploadFor(episodeKey)?.status === "completed") {
        commit("removeUpload", episodeKey)
      }
    }, PRUNE_DELAY)
  )
}

export const failUpload = async (context, { episodeKey, errorKey, processed = false }) => {
  const { commit, dispatch, getters } = context
  const upload = getters.uploadFor(episodeKey)

  stopTransfer(episodeKey)

  if (!upload) return

  if (discards.has(episodeKey)) {
    if (processed) {
      await wait(RESAVE_AFTER_ERROR_DELAY)
    }

    await deleteDiscarded(context, episodeKey)
    return
  }

  commit("updateUpload", { episodeKey, fields: { status: "failed", errorKey, secondsLeft: null } })
  dispatch("syncUnloadGuard")

  notificationError(
    `${i18n.t("episodes.upload_notifications.failed", { title: upload.title })} ${i18n.t(errorKey)}`,
    i18n.t("episodes.upload_error_title")
  )

  if (pendingSaves.has(episodeKey)) {
    if (processed) {
      await wait(RESAVE_AFTER_ERROR_DELAY)
    }

    await dispatch("flushPendingSave", episodeKey)
  }
}

export const retryUpload = ({ dispatch, getters }, episodeKey) => {
  const upload = getters.uploadFor(episodeKey)
  const file = files.get(episodeKey)

  if (!upload || !file) return

  return dispatch("startUpload", { ...upload, file })
}

export const cancelUpload = async ({ commit, dispatch }, episodeKey) => {
  stopTransfer(episodeKey)
  files.delete(episodeKey)
  commit("removeUpload", episodeKey)
  dispatch("syncUnloadGuard")

  await dispatch("flushPendingSave", episodeKey)
}

export const dismissUpload = ({ commit, dispatch, getters }, episodeKey) => {
  if (getters.isUploadActive(episodeKey)) return

  stopTransfer(episodeKey)
  files.delete(episodeKey)
  commit("removeUpload", episodeKey)
  dispatch("syncUnloadGuard")
}

export const rememberSave = ({ commit, getters }, { payload, isAiPodcast }) => {
  const episodeKey = payload.key

  if (!getters.uploadFor(episodeKey)) return

  commit("updateUpload", { episodeKey, fields: { title: payload.title || getters.uploadFor(episodeKey).title } })

  if (getters.isUploadActive(episodeKey)) {
    pendingSaves.set(episodeKey, { payload, isAiPodcast })
  }
}

export const discardPendingSave = (context, episodeKey) => {
  pendingSaves.delete(episodeKey)
}

export const cancelAllUploads = ({ commit, dispatch, state }) => {
  Object.keys(state.uploads).forEach(stopTransfer)
  files.clear()
  pendingSaves.clear()
  discards.clear()
  commit("reset")
  dispatch("syncUnloadGuard")
}
